import React, { useEffect, useMemo, useState } from "react";
import { TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import {
  Plus, RefreshCw, Crown, UserCheck, UserX, Trash2, Sparkles,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Search, X,
} from "lucide-react";
import TestUserCleanup from "./TestUserCleanup";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const fmtTime = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Never";

// Sortable columns — spans mirror the row grid exactly (12 cols)
const USER_COLUMNS = [
  { key: "email", label: "Email", span: "col-span-3" },
  { key: "status", label: "Status", span: "col-span-1" },
  { key: "active", label: "Active", span: "col-span-1" },
  { key: "role", label: "Role", span: "col-span-1" },
  { key: "created_at", label: "Joined", span: "col-span-2" },
  { key: "last_login", label: "Last Login", span: "col-span-2" },
  { key: null, label: "Actions", span: "col-span-2" },
];

const sortValue = (u, col) => ({
  email: (u.email || "").toLowerCase(),
  status: u.is_confirmed ? 1 : 0,
  active: u.is_active !== false ? 1 : 0,
  role: u.is_admin ? 2 : (u.is_pro ? 1 : 0),
  created_at: u.created_at ? new Date(u.created_at).getTime() : 0,
  last_login: u.last_login ? new Date(u.last_login).getTime() : 0,
}[col]);

export default function UsersTab({
  dm, users, usersPage, setUsersPage, usersPageSize, setUsersPageSize,
  usersPgSizeOpen, setUsersPgSizeOpen, usersSortDir, setUsersSortDir,
  loadData, showAddUser, setShowAddUser, newUserEmail, setNewUserEmail,
  newUserIsAdmin, setNewUserIsAdmin,
  newUserIsPro, setNewUserIsPro,
  newUserSendLink, setNewUserSendLink,
  addingUser, addUser,
  toggleAdmin, togglePro, toggleActive, confirmUser, deleteUser,
  authHeader,
}) {
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortCol, setSortCol] = useState("created_at");
  const [sortAsc, setSortAsc] = useState(false);

  const sorted = useMemo(() => {
    const q = searchFilter.trim().toLowerCase();
    const list = users.filter(u =>
      (!q || (u.email || "").toLowerCase().includes(q)) &&
      (statusFilter === "all" ||
        (statusFilter === "confirmed" ? u.is_confirmed
          : statusFilter === "pending" ? !u.is_confirmed
          : u.is_active === false)) &&
      (roleFilter === "all" ||
        (roleFilter === "admin" ? u.is_admin
          : roleFilter === "pro" ? u.is_pro
          : (!u.is_admin && !u.is_pro)))
    );
    return [...list].sort((a, b) => {
      const va = sortValue(a, sortCol), vb = sortValue(b, sortCol);
      const c = typeof va === "string" ? va.localeCompare(vb) : va - vb;
      return sortAsc ? c : -c;
    });
  }, [users, searchFilter, statusFilter, roleFilter, sortCol, sortAsc]);

  const filtersActive = !!searchFilter.trim() || statusFilter !== "all" || roleFilter !== "all";
  useEffect(() => { setUsersPage(1); }, [searchFilter, statusFilter, roleFilter, sortCol, sortAsc]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSort = (key) => {
    if (!key) return;
    if (sortCol === key) setSortAsc(a => !a);
    else { setSortCol(key); setSortAsc(key === "email"); }
  };

  const selectCls = `px-3 py-2 rounded-full category-tag border-0 outline-none cursor-pointer ${dm ? "bg-[#1e2627] text-slate-300" : "bg-[#f3f4f5] text-[#3b494c]"}`;
  const totalPages = Math.max(1, Math.ceil(sorted.length / usersPageSize));

  return (
    <TabsContent value="users">
      <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5`}>
        <div>
          <h2 className={`editorial-heading text-xl sm:text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Registered Users</h2>
          <p className={`category-tag mt-0.5 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{users.length} total · {users.filter(u => u.is_confirmed).length} confirmed · {users.filter(u => u.is_admin).length} admin · {users.filter(u => u.is_pro).length} PRO</p>
        </div>
        <div className="flex items-center gap-2">
          <TestUserCleanup dm={dm} authHeader={authHeader} onDone={() => loadData(usersSortDir)} />
          <button
            onClick={() => setShowAddUser(v => !v)}
            data-testid="add-user-btn"
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full category-tag transition-all ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
          >
            <Plus className="w-3.5 h-3.5" />Add User
          </button>
        </div>
      </div>

      {showAddUser && (
        <form onSubmit={addUser} data-testid="add-user-form"
          className={`rounded-2xl p-6 mb-5 ${dm ? "bg-[#1a2122]" : "bg-white"}`}
          style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.12), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <h3 className={`editorial-heading text-lg font-medium mb-4 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Add New User</h3>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div className="flex-1">
              <label className={`category-tag block mb-1.5 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Email address</label>
              <Input
                type="email" required
                value={newUserEmail}
                onChange={e => setNewUserEmail(e.target.value)}
                placeholder="user@example.com"
                data-testid="add-user-email-input"
                className={`border-0 ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5] placeholder:text-[#bac9cc]"}`}
              />
            </div>
            <div className="flex items-center gap-4 pb-2.5">
              <label className={`flex items-center gap-2 cursor-pointer category-tag ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                <input type="checkbox" checked={newUserIsAdmin} onChange={e => setNewUserIsAdmin(e.target.checked)}
                  data-testid="add-user-admin-checkbox"
                  className="w-3.5 h-3.5 accent-cyan-400 rounded" />
                Make admin
              </label>
              <label className={`flex items-center gap-2 cursor-pointer category-tag ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                <input type="checkbox" checked={newUserIsPro} onChange={e => setNewUserIsPro(e.target.checked)}
                  data-testid="add-user-pro-checkbox"
                  className="w-3.5 h-3.5 accent-violet-400 rounded" />
                Grant PRO
              </label>
              <label className={`flex items-center gap-2 cursor-pointer category-tag ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                <input type="checkbox" checked={newUserSendLink} onChange={e => setNewUserSendLink(e.target.checked)}
                  data-testid="add-user-send-link-checkbox"
                  className="w-3.5 h-3.5 accent-cyan-400 rounded" />
                Send magic link
              </label>
            </div>
            <div className="flex gap-2 pb-2.5">
              <button type="submit" disabled={addingUser || !newUserEmail.trim()}
                data-testid="add-user-submit-btn"
                className={`px-5 py-2.5 rounded-full category-tag transition-all disabled:opacity-50 flex items-center gap-1.5 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}>
                {addingUser ? <><RefreshCw className="w-3 h-3 animate-spin" />Adding…</> : <><Plus className="w-3 h-3" />Add User</>}
              </button>
              <button type="button" onClick={() => { setShowAddUser(false); setNewUserEmail(""); setNewUserIsAdmin(false); setNewUserIsPro(false); setNewUserSendLink(true); }}
                className={`px-4 py-2.5 rounded-full category-tag transition-all ${dm ? "bg-[#252e2f] text-slate-400 hover:bg-[#303b3c]" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e7e8e9]"}`}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Filter toolbar — search + status + role (client-side, full list is loaded) */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <div className={`flex items-center gap-2 px-3.5 rounded-full flex-1 min-w-0 ${dm ? "bg-[#1e2627]" : "bg-white"}`}
          style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.18)", height: 38 }}>
          <Search className={`w-3.5 h-3.5 flex-shrink-0 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`} />
          <input
            data-testid="users-search-input"
            type="text"
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            placeholder="Filter by email…"
            className={`flex-1 bg-transparent text-sm outline-none min-w-0 ${dm ? "text-slate-200 placeholder:text-slate-600" : "text-[#191c1d] placeholder:text-[#bac9cc]"}`}
          />
          {searchFilter && (
            <button data-testid="users-search-clear" onClick={() => setSearchFilter("")}
              className={dm ? "text-slate-600 hover:text-slate-400" : "text-[#bac9cc] hover:text-[#6b7a7d]"}>
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <select data-testid="users-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={selectCls}>
          <option value="all">All statuses</option>
          <option value="confirmed">Confirmed</option>
          <option value="pending">Pending</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>
        <select data-testid="users-role-filter" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className={selectCls}>
          <option value="all">All roles</option>
          <option value="admin">Admin</option>
          <option value="pro">PRO</option>
          <option value="user">Standard</option>
        </select>
        {filtersActive && (
          <button
            data-testid="users-clear-filters"
            onClick={() => { setSearchFilter(""); setStatusFilter("all"); setRoleFilter("all"); }}
            className={`px-3 py-2 rounded-full category-tag font-bold transition-colors ${dm ? "text-cyan-400 hover:bg-cyan-400/10" : "text-[#006874] hover:bg-[#006874]/8"}`}
          >
            Clear
          </button>
        )}
      </div>

      <div className="overflow-x-auto no-scrollbar rounded-2xl" style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
        <div className={`min-w-[680px] rounded-2xl overflow-hidden ${dm ? "bg-[#1a2122]" : "bg-white"}`}>
        <div className={`grid grid-cols-12 px-5 py-3 border-b ${dm ? "border-white/5" : "border-black/4"}`}>
          {USER_COLUMNS.map((col) => (
            col.key ? (
              <button
                key={col.label}
                data-testid={`users-sort-${col.key}`}
                onClick={() => onSort(col.key)}
                title={`Sort by ${col.label}`}
                className={`category-tag flex items-center gap-1 text-left select-none transition-colors ${col.span} ${
                  sortCol === col.key
                    ? (dm ? "text-cyan-400" : "text-[#006874]")
                    : (dm ? "text-slate-600 hover:text-slate-400" : "text-[#bac9cc] hover:text-[#6b7a7d]")
                }`}
              >
                {col.label}
                {sortCol === col.key && (sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
              </button>
            ) : (
              <div key={col.label} className={`category-tag text-right ${col.span} ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                {col.label}
              </div>
            )
          ))}
        </div>

        {sorted.length === 0 && (
          <div data-testid="users-empty-state" className={`px-5 py-12 text-center category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            No users match your filters.
          </div>
        )}

        {sorted.slice((usersPage - 1) * usersPageSize, usersPage * usersPageSize).map((user, idx, arr) => (
          <div key={user.id} className={`grid grid-cols-12 px-5 py-4 items-center transition-colors ${dm ? "hover:bg-[#1e2627] border-b border-white/4" : "hover:bg-[#fafbfb] border-b border-black/3"} ${idx === arr.length - 1 ? "border-b-0" : ""}`}>
            <div className="col-span-3 flex items-center gap-2 min-w-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold uppercase ${user.is_admin ? (dm ? "bg-cyan-400/15 text-cyan-400" : "bg-[#006874]/10 text-[#006874]") : (dm ? "bg-[#252e2f] text-slate-400" : "bg-[#f3f4f5] text-[#6b7a7d]")}`}>
                {user.email[0].toUpperCase()}
              </div>
              <span className={`text-sm font-medium truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{user.email}</span>
            </div>
            <div className="col-span-1">
              {user.is_confirmed
                ? <span className={`px-2.5 py-1 rounded-full category-tag ${dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700"}`}>Confirmed</span>
                : <span className={`px-2.5 py-1 rounded-full category-tag ${dm ? "bg-amber-400/10 text-amber-400" : "bg-amber-50 text-amber-700"}`}>Pending</span>}
            </div>
            <div className="col-span-1">
              {user.is_active !== false
                ? <span className={`px-2.5 py-1 rounded-full category-tag ${dm ? "bg-[#252e2f] text-slate-400" : "bg-[#f3f4f5] text-[#6b7a7d]"}`}>Active</span>
                : <span title={user.unsubscribe_reason ? `Reason: ${user.unsubscribe_reason}` : ""} className={`px-2.5 py-1 rounded-full category-tag ${dm ? "bg-red-400/10 text-red-400" : "bg-red-50 text-red-600"}`}>Unsubscribed</span>}
            </div>
            <div className="col-span-1 flex flex-col gap-1">
              {user.is_admin
                ? <span className={`px-2.5 py-1 rounded-full category-tag flex items-center gap-1 w-fit ${dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}><Crown className="w-2.5 h-2.5" />Admin</span>
                : (!user.is_pro && <span className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>User</span>)}
              {user.is_pro && (
                <span
                  title={user.pro_grant_source ? `PRO via ${user.pro_grant_source}${user.pro_granted_at ? ` · ${fmtDate(user.pro_granted_at)}` : ""}` : "PRO access (web + mobile)"}
                  className={`px-2.5 py-1 rounded-full category-tag flex items-center gap-1 w-fit ${dm ? "bg-violet-400/10 text-violet-300" : "bg-violet-50 text-violet-700"}`}
                >
                  <Sparkles className="w-2.5 h-2.5" />PRO
                </span>
              )}
            </div>
            <div className={`col-span-2 category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{fmtDate(user.created_at)}</div>
            <div className={`col-span-2 flex items-center gap-1.5 category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
              {user.brevo_synced
                ? <span title="Synced to Brevo" className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${dm ? "bg-emerald-400" : "bg-emerald-500"}`} />
                : <span title="Not yet synced to Brevo" className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${dm ? "bg-slate-600" : "bg-[#e7e8e9]"}`} />}
              {fmtTime(user.last_login)}
            </div>
            <div className="col-span-2 flex items-center justify-end gap-1">
              {!user.is_confirmed && (
                <button onClick={() => confirmUser(user.id)} title="Confirm user" className={`p-1.5 rounded-lg transition-colors ${dm ? "text-emerald-400 hover:bg-emerald-400/10" : "text-emerald-600 hover:bg-emerald-50"}`}>
                  <UserCheck className="w-3.5 h-3.5" />
                </button>
              )}
              <button onClick={() => toggleAdmin(user.id, user.is_admin)} title={user.is_admin ? "Remove admin" : "Make admin"} className={`p-1.5 rounded-lg transition-colors ${user.is_admin ? (dm ? "text-cyan-400 hover:bg-cyan-400/10" : "text-[#006874] hover:bg-[#006874]/8") : (dm ? "text-slate-500 hover:bg-[#252e2f]" : "text-[#bac9cc] hover:bg-[#f3f4f5]")}`}>
                <Crown className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => togglePro(user.id, !!user.is_pro)}
                title={user.is_pro ? "Revoke PRO access (web + mobile)" : "Grant PRO access (web + mobile)"}
                data-testid={`toggle-pro-btn-${user.id}`}
                className={`p-1.5 rounded-lg transition-colors ${user.is_pro ? (dm ? "text-violet-300 hover:bg-violet-400/10" : "text-violet-700 hover:bg-violet-50") : (dm ? "text-slate-500 hover:bg-[#252e2f]" : "text-[#bac9cc] hover:bg-[#f3f4f5]")}`}
              >
                <Sparkles className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => toggleActive(user.id, user.is_active !== false)} title={user.is_active !== false ? "Suspend" : "Activate"} className={`p-1.5 rounded-lg transition-colors ${dm ? "text-slate-500 hover:bg-[#252e2f] hover:text-amber-400" : "text-[#bac9cc] hover:bg-amber-50 hover:text-amber-600"}`}>
                {user.is_active !== false ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => deleteUser(user.id)} title="Delete user" className={`p-1.5 rounded-lg transition-colors ${dm ? "text-slate-600 hover:bg-red-400/10 hover:text-red-400" : "text-[#e7e8e9] hover:bg-red-50 hover:text-red-500"}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
        </div>
      </div>

      {sorted.length > 0 && (
        <div className={`flex items-center justify-between px-5 py-3 mt-0 rounded-b-2xl border-t ${dm ? "border-white/5 bg-[#1a2122]" : "border-black/4 bg-white"}`}
          style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className="relative flex items-center gap-2">
            <button
              data-testid="users-page-size-btn"
              onClick={() => setUsersPgSizeOpen(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg category-tag transition-colors ${dm ? "bg-[#252e2f] text-slate-300 hover:bg-[#303b3c]" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#ecedef]"}`}
            >
              {usersPageSize}
              <ChevronUp className={`w-3 h-3 transition-transform ${usersPgSizeOpen ? "" : "rotate-180"}`} />
            </button>
            <span className={`category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>Rows per page</span>
            {usersPgSizeOpen && (
              <div className={`absolute bottom-full left-0 mb-1 rounded-xl overflow-hidden z-20 ${dm ? "bg-[#1e2627] border border-white/10" : "bg-white border border-[#e7e8e9]"}`}
                style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
                {[20, 50, 100].map(size => (
                  <button key={size} data-testid={`page-size-option-${size}`}
                    onClick={() => { setUsersPageSize(size); setUsersPage(1); setUsersPgSizeOpen(false); }}
                    className={`w-full px-5 py-2.5 text-left category-tag transition-colors ${
                      size === usersPageSize
                        ? (dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]")
                        : (dm ? "text-slate-300 hover:bg-[#252e2f]" : "text-[#3b494c] hover:bg-[#f3f4f5]")
                    }`}>
                    {size}
                  </button>
                ))}
              </div>
            )}
          </div>

          <span data-testid="users-range" className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            {(usersPage - 1) * usersPageSize + 1}–{Math.min(usersPage * usersPageSize, sorted.length)} of {sorted.length}
            {filtersActive && sorted.length !== users.length && ` (filtered from ${users.length})`}
          </span>

          <div className="flex items-center gap-2">
            <button data-testid="users-prev-page"
              onClick={() => setUsersPage(p => Math.max(1, p - 1))}
              disabled={usersPage <= 1}
              className={`p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${dm ? "text-slate-400 hover:bg-[#252e2f]" : "text-[#6b7a7d] hover:bg-[#f3f4f5]"}`}>
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <select
              data-testid="users-page-select"
              value={usersPage}
              onChange={e => setUsersPage(Number(e.target.value))}
              className={`px-2 py-1.5 rounded-lg category-tag border-0 outline-none cursor-pointer ${dm ? "bg-[#252e2f] text-slate-300" : "bg-[#f3f4f5] text-[#3b494c]"}`}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <span className={`category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>of {totalPages} {totalPages === 1 ? "page" : "pages"}</span>
            <button data-testid="users-next-page"
              onClick={() => setUsersPage(p => Math.min(totalPages, p + 1))}
              disabled={usersPage >= totalPages}
              className={`p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${dm ? "text-slate-400 hover:bg-[#252e2f]" : "text-[#6b7a7d] hover:bg-[#f3f4f5]"}`}>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </TabsContent>
  );
}
